import { useMemo } from 'react';
import { Input } from '@/shared/ui';
import { PRODUCT_TYPES, type BrandDNA, type ProductType } from '@/entities/brand';

const PRODUCT_TYPE_LABELS: Record<ProductType, string> = {
  app: 'App / digital product (the "product" is a UI screenshot)',
  physical: 'Physical product (CPG, apparel, hardware, food)',
  service: 'Service / consultancy (no shippable product or app)',
};

const COLOR_FIELDS = ['primaryColor', 'accentColor', 'background'] as const;
const SHORT_FIELDS = ['typography', 'headlineTone'] as const;
const LONG_FIELDS = [
  'photographyStyle',
  'photographyMood',
  'productDescription',
  'usp',
  'audience',
  'promptModifier',
] as const;

const LABELS: Record<keyof BrandDNA, string> = {
  productType: 'Product type',
  primaryColor: 'Primary color',
  accentColor: 'Accent color',
  background: 'Background color',
  typography: 'Typography',
  photographyStyle: 'Photography style',
  photographyMood: 'Photography mood',
  productDescription: 'Product description',
  usp: 'Unique selling proposition',
  audience: 'Target audience',
  headlineTone: 'Headline tone',
  promptModifier: 'Prompt modifier (optional, prepended to every image prompt)',
};

export const EMPTY_DNA: BrandDNA = {
  productType: 'physical',
  primaryColor: '#000000',
  accentColor: '#ff5500',
  background: '#fafafa',
  typography: '',
  photographyStyle: '',
  photographyMood: '',
  productDescription: '',
  usp: '',
  audience: '',
  headlineTone: '',
  promptModifier: '',
};

interface Props {
  value: BrandDNA;
  onChange: (next: BrandDNA) => void;
}

export function BrandDnaForm({ value, onChange }: Props) {
  const setField = useMemo(
    () =>
      <K extends keyof BrandDNA>(key: K, v: BrandDNA[K]) =>
        onChange({ ...value, [key]: v }),
    [value, onChange],
  );

  const promptModLen = value.promptModifier?.length ?? 0;
  const promptModError =
    promptModLen > 800 ? `Too long (max 800, currently ${promptModLen}).` : null;

  return (
    <div className="space-y-5">
      <section>
        <h3 className="text-sm font-medium">Product type</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Templates branch on this: app brands get screenshot-aware prompts, physical brands get product photography.
        </p>
        <div className="mt-2">
          <Field label={LABELS.productType}>
            <select
              className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm"
              value={value.productType}
              onChange={(e) => setField('productType', e.target.value as ProductType)}
            >
              {PRODUCT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {PRODUCT_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </section>

      <section>
        <h3 className="text-sm font-medium">Colors</h3>
        <div className="mt-2 grid gap-3 sm:grid-cols-2">
          {COLOR_FIELDS.map((k) => (
            <ColorField
              key={k}
              label={LABELS[k]}
              value={value[k]}
              onChange={(v) => setField(k, v)}
            />
          ))}
        </div>
      </section>

      <section>
        <h3 className="text-sm font-medium">Typography</h3>
        <div className="mt-2 grid gap-3 sm:grid-cols-2">
          {SHORT_FIELDS.map((k) => (
            <Field key={k} label={LABELS[k]}>
              <Input
                value={value[k]}
                onChange={(e) => setField(k, e.target.value)}
              />
            </Field>
          ))}
        </div>
      </section>

      <section>
        <h3 className="text-sm font-medium">Brand essence</h3>
        <div className="mt-2 grid gap-3">
          {LONG_FIELDS.map((k) => (
            <Field key={k} label={LABELS[k]}>
              <textarea
                rows={k === 'promptModifier' ? 4 : 2}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/40"
                value={value[k] ?? ''}
                onChange={(e) => setField(k, e.target.value)}
              />
              {k === 'promptModifier' && promptModError ? (
                <p className="text-xs text-destructive">{promptModError}</p>
              ) : null}
            </Field>
          ))}
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-sm text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const colorValue = /^#[0-9a-fA-F]{6}$/.test(value) ? value : '#000000';
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-sm text-muted-foreground">{label}</span>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={colorValue}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 w-12 cursor-pointer rounded border border-border bg-background"
        />
        <Input value={value} onChange={(e) => onChange(e.target.value)} />
      </div>
    </label>
  );
}
